import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, SectionTitle } from '../../shared/components';
import { StateChip } from '../components/Chips';
import EmployeeWorkloadDialog from '../components/EmployeeWorkloadDialog';
import KpiTile from '../components/KpiTile';
import { useTaskMeta } from '../components/useTaskMeta';
import WorkloadTable, { fromEmployee, fromGroup } from '../components/WorkloadTable';
import { workloadApi } from '../tasks.api';
import type { AdminDashboard, GroupWorkload, ManagerDashboard, OrganizationWorkload } from '../tasks.types';
import { errorMessage, fmtHours, fmtPct, isoDate, today, weekStart } from '../tasks.utils';

type View = 'team' | 'departments' | 'organization' | 'admin';

/** Manager and admin workload: who is overloaded, who has room, and where the hours are. */
export default function WorkloadPage() {
  const navigate = useNavigate();
  const meta = useTaskMeta();
  const canTeam = Boolean(meta?.permissions.WORKLOAD_VIEW_TEAM);
  const canOrg = Boolean(meta?.permissions.WORKLOAD_VIEW_ORGANIZATION);
  const [view, setView] = useState<View>('team');
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState(weekStart(today()));
  const [to, setTo] = useState(isoDate(dayjs(weekStart(today())).add(6, 'day')));
  const [departmentId, setDepartmentId] = useState('');
  const [mgr, setMgr] = useState<ManagerDashboard | null>(null);
  const [depts, setDepts] = useState<GroupWorkload[]>([]);
  const [org, setOrg] = useState<OrganizationWorkload | null>(null);
  const [admin, setAdmin] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    try {
      if (view === 'team') setMgr(await workloadApi.manager(date, departmentId ? Number(departmentId) : undefined));
      if (view === 'departments') setDepts(await workloadApi.departments(from, to));
      if (view === 'organization') setOrg(await workloadApi.organization(from, to));
      if (view === 'admin') setAdmin(await workloadApi.admin(from, to));
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load workload'));
    } finally {
      setLoading(false);
    }
  }, [meta, view, date, from, to, departmentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (meta && !canTeam) return <Alert severity='info'>Workload views are for managers. Your own workload is on My Work.</Alert>;

  const range = (
    <Stack direction='row' gap={1}>
      <TextField type='date' size='small' label='From' InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
      <TextField type='date' size='small' label='To' InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
    </Stack>
  );

  return (
    <Stack gap={3}>
      <SectionTitle title='WORKLOAD' />
      <Tabs value={view} onChange={(_, v) => setView(v)}>
        <Tab value='team' label='Team' />
        {canOrg && <Tab value='departments' label='Departments' />}
        {canOrg && <Tab value='organization' label='Organization' />}
        {canOrg && <Tab value='admin' label='Admin dashboard' />}
      </Tabs>
      {loading && <LinearProgress />}

      {view === 'team' && (
        <>
          <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
            <TextField type='date' size='small' label='Date' InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
            <TextField select size='small' label='Department' sx={{ minWidth: 200 }} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <MenuItem value=''>Everyone</MenuItem>
              {meta?.departments.map((d) => <MenuItem key={d.id} value={String(d.id)}>{d.label}</MenuItem>)}
            </TextField>
          </Stack>
          {mgr && (
            <>
              <Stack direction='row' gap={2} flexWrap='wrap'>
                <KpiTile label='Team size' value={mgr.teamSize} icon='mdi:account-group-outline' />
                <KpiTile label='Active tasks' value={mgr.activeTasks} icon='mdi:clipboard-play-outline' onClick={() => navigate('/tasks')} />
                <KpiTile label='Completed today' value={mgr.completedToday} icon='mdi:flag-checkered' />
                <KpiTile label='Overdue' value={mgr.overdueTasks} icon='mdi:calendar-alert' alert={mgr.overdueTasks > 0} onClick={() => navigate('/tasks?overdue=true')} />
                <KpiTile label='Blocked' value={mgr.blockedTasks} icon='mdi:block-helper' alert={mgr.blockedTasks > 0} onClick={() => navigate('/tasks?status=BLOCKED')} />
                <KpiTile label='Pending reassignments' value={mgr.pendingReassignments} icon='mdi:account-switch-outline' alert={mgr.pendingReassignments > 0} onClick={() => navigate('/tasks/reassignments')} />
                <KpiTile label='Overloaded' value={mgr.overloadedEmployees} icon='mdi:alert-octagon-outline' alert={mgr.overloadedEmployees > 0} />
                <KpiTile label='Underutilized' value={mgr.underutilizedEmployees} icon='mdi:battery-low' />
              </Stack>
              <Typography variant='body2' color='text.secondary'>
                {dayjs(date).format('DD MMM YYYY')}: {fmtHours(mgr.totalAllocatedHours)} planned of {fmtHours(mgr.totalCapacityHours)} capacity ·{' '}
                {fmtHours(mgr.totalActualHours)} logged · {fmtHours(mgr.totalRemainingHours)} remaining · {fmtHours(mgr.activeAllocationHours)} of open work across the team
              </Typography>
              <Card title='EMPLOYEE WORKLOAD' sx={{ py: 2 }}>
                <WorkloadTable rows={mgr.employees.map(fromEmployee)} onRow={(k) => setEmployee(Number(k))} />
              </Card>
              {mgr.reassignmentQueue.length > 0 && (
                <Card title='RELEASE REQUESTS WAITING' sx={{ py: 2 }}>
                  {mgr.reassignmentQueue.map((r) => (
                    <Stack key={r.id} direction='row' gap={2} alignItems='center'>
                      <StateChip status={r.status} label={r.statusLabel} />
                      <Typography variant='body2'>{r.taskCode} · {r.taskTitle} — {r.fromEmployee?.name}: {r.reason}</Typography>
                      <Button size='small' sx={{ ml: 'auto' }} onClick={() => navigate(`/tasks/${r.taskId}`)}>Review</Button>
                    </Stack>
                  ))}
                </Card>
              )}
            </>
          )}
        </>
      )}

      {view === 'departments' && (
        <>
          {range}
          <Card title='DEPARTMENT WORKLOAD' sx={{ py: 2 }}>
            <WorkloadTable nameLabel='Department' rows={depts.map(fromGroup)} onRow={(k) => { setDepartmentId(String(k)); setView('team'); }} />
          </Card>
        </>
      )}

      {view === 'organization' && org && (
        <>
          {range}
          <Stack direction='row' gap={2} flexWrap='wrap'>
            <KpiTile label='Capacity' value={fmtHours(org.totals.capacityHours)} icon='mdi:gauge' />
            <KpiTile label='Allocated' value={fmtHours(org.totals.allocatedHours)} icon='mdi:calendar-check' />
            <KpiTile label='Actual' value={fmtHours(org.totals.actualHours)} icon='mdi:clock-check-outline' />
            <KpiTile label='Utilization' value={fmtPct(org.totals.utilization)} icon='mdi:chart-donut' />
            <KpiTile label='Active allocation' value={fmtHours(org.activeAllocationHours)} icon='mdi:briefcase-clock-outline' />
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            Active allocation counts only open assignments. Historically {fmtHours(org.historicalAllocationHours)} has been allocated, of which{' '}
            {fmtHours(org.releasedHours)} was released and passed on — those hours are not counted twice.
          </Typography>
          <Card title='BY DEPARTMENT' sx={{ py: 2 }}>
            <WorkloadTable nameLabel='Department' rows={org.departments.map(fromGroup)} />
          </Card>
        </>
      )}

      {view === 'admin' && admin && (
        <>
          {range}
          <Stack direction='row' gap={2} flexWrap='wrap'>
            <KpiTile label='Active tasks' value={admin.activeTasks} icon='mdi:clipboard-play-outline' />
            <KpiTile label='Backlog / released' value={admin.backlogTasks} icon='mdi:tray-full' />
            <KpiTile label='Completed' value={admin.completedTasks} icon='mdi:flag-checkered' />
            <KpiTile label='Cancelled' value={admin.cancelledTasks} icon='mdi:cancel' />
            <KpiTile label='Reassigned' value={admin.reassignedTasks} icon='mdi:account-switch-outline' />
            <KpiTile label='Pending reassignments' value={admin.pendingReassignments} icon='mdi:timer-sand' alert={admin.pendingReassignments > 0} />
            <KpiTile label='Overdue' value={admin.overdueTasks} icon='mdi:calendar-alert' alert={admin.overdueTasks > 0} />
            <KpiTile label='Blocked' value={admin.blockedTasks} icon='mdi:block-helper' alert={admin.blockedTasks > 0} />
          </Stack>
          <Card title='ORGANIZATION' sx={{ py: 2 }}>
            <WorkloadTable nameLabel='' rows={[fromGroup(admin.organization.totals)]} />
          </Card>
          <Card title='DEPARTMENTS' sx={{ py: 2 }}>
            <WorkloadTable nameLabel='Department' rows={admin.organization.departments.map(fromGroup)} />
          </Card>
          <Card title={`OVERLOADED EMPLOYEES (${admin.overloadedEmployees.length})`} sx={{ py: 2 }}>
            <WorkloadTable rows={admin.overloadedEmployees.map(fromEmployee)} onRow={(k) => setEmployee(Number(k))} />
          </Card>
          <Card title={`UNDERUTILIZED EMPLOYEES (${admin.underutilizedEmployees.length})`} sx={{ py: 2 }}>
            <WorkloadTable rows={admin.underutilizedEmployees.map(fromEmployee)} onRow={(k) => setEmployee(Number(k))} />
          </Card>
        </>
      )}

      <EmployeeWorkloadDialog employeeId={employee} date={view === 'team' ? date : from} onClose={() => setEmployee(null)} />
    </Stack>
  );
}
